'use client';

import ActivityProductGrid from '@/app/(activity)/components/ActivityProductGrid';
import FaqAccordion from '@/app/(activity)/components/FaqAccordion';
import HotProductSection from '@/app/(activity)/components/HotProductSection';
import { usePageCache } from '@/hooks/usePageCache';
import { useStore } from '@/providers/app-store-provider';
import '@/styles/animations.css';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import React, { useEffect } from 'react';
import StaticFaqForSeo from '../../components/StaticFaqForSeo';
import { useDrivingGlassesTracking } from '../hooks/useDrivingGlassesTracking';
import Banner from './Banner';
import ClearDriving from './ClearDriving';
import DrivingBenefit from './DrivingBenefit';
import StepBox from './StepBox';

// 添加必要的CSS动画样式
const styles = `
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }
`;

interface DrivingGlassesData {
  banner: {
    title: string;
    subtitle: string;
    description: string;
    imageUrl: string;
    buttonText?: string;
  };
  hot_product: {
    group: string;
    title: string;
  };
  list_part: {
    group: string;
    title: string;
  };
  faq_part: {
    title: string;
    faq_list: Array<{
      question: string;
      answer: string;
    }>;
  };
  driving_intro?: {
    title?: string;
    intro_arr?: Array<{
      image?: string;
      title?: string;
      description?: string;
    }>;
  };
  driving_benefits?: {
    title?: string;
    function_intro?: Array<{
      image?: string;
      word?: string;
      title?: string;
    }>;
    lens_introduction?: Array<{
      image?: string;
      title?: string;
      description?: string;
    }>;
  };
  customize_glasses?: {
    title?: string;
    step_arr?: Array<{
      word1?: string;
      word2?: string;
    }>;
  };
  clear_driving?: {
    title?: string;
    description?: string;
    normal_img?: string;
    clear_img?: string;
    normal_word?: string;
    clear_word?: string;
  };
  [key: string]: any;
}

interface Props {
  drivingData: DrivingGlassesData;
  deviceType: EPlatform;
  hotProducts: any[];
}

export default function DrivingGlassesContent({ drivingData, deviceType, hotProducts }: Props) {
  const isMobile = deviceType !== EPlatform.WEB;

  // App相关状态
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  // 使用埋点hooks
  const {
    trackKnowledgeTopicPageView,
    trackKnowledgeTopicPageBannerExp,
    trackKnowledgeTopicPageBannerClick,
    trackKnowledgeTopicPageMktExp,
    trackKnowledgeTopicPageProductExp,
    trackKnowledgeTopicPageProductClick,
  } = useDrivingGlassesTracking();

  // 使用页面缓存 hook 来保存页面状态和控制动画
  const { pageState, isFromCache } = usePageCache({
    pageKey: 'driving-glasses-page-v1',
    initialData: {
      drivingData,
      deviceType,
      hotProducts,
      timestamp: Date.now(),
    },
  });

  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 清空当前用户信息
      setToken('');
      setUserInfo({});

      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (token) => {
          logger.log('App登录token:', token);
          if (token !== 'ClearToken') {
            setToken(token);
            // 获取用户信息
            await refreshAccountInfo();
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (error) {
      logger.error('App通信初始化失败:', error);
    }
  };

  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 页面浏览埋点 - 组件加载时触发
  useEffect(() => {
    trackKnowledgeTopicPageView();
  }, [trackKnowledgeTopicPageView]);

  // 组件卸载时清理动画 - 对应Vue中的removeAnimation，只在PC端执行
  React.useEffect(() => {
    // 只在PC端添加动画相关逻辑
    if (isMobile) return;

    return () => {
      try {
        const animationClasses = [
          'blueSwiperFadeInUpAnimate',
          'fadeInDownAnimate',
          'scaleToBigAnimate',
          'fadeInUpAnimate',
        ];

        animationClasses.forEach((className) => {
          const elements = document.querySelectorAll(`.${className}`);
          elements.forEach((element) => {
            element.classList.remove(className);
            if (element.classList.contains('opacity0')) {
              element.classList.remove('opacity0');
            }
            const htmlElement = element as HTMLElement;
            htmlElement.style.transform = 'translateY(0)';
          });
        });
      } catch {
        // 不做处理
      }
    };
  }, [isMobile]);

  return (
    <>
      {/* 注入样式 */}
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      <div className={`w-full min-h-screen ${isMobile ? 'driving-wap' : 'driving-pc'}`}>
        {/* 黑色背景部分 */}
        <div className="bg-black text-white relative">
          {/* Banner 模块 */}
          <Banner
            banner={pageState.drivingData.banner}
            isMobile={isMobile}
            onBannerExposure={() => {
              if (pageState.drivingData.banner?.group) {
                trackKnowledgeTopicPageBannerExp({
                  group_id: pageState.drivingData.banner.group,
                });
              }
            }}
            onBannerClick={() => {
              if (pageState.drivingData.banner?.group) {
                trackKnowledgeTopicPageBannerClick({
                  group_id: pageState.drivingData.banner.group,
                });
              }
            }}
          />

          {/* 清晰驾驶模块 */}
          <ClearDriving
            drivingData={pageState.drivingData}
            isMobile={isMobile}
            isFromCache={isFromCache}
            onExposure={() => {
              trackKnowledgeTopicPageMktExp({
                mkt_name: '清晰驾驶',
              });
            }}
          />

          {/* 热卖产品模块 */}
          <HotProductSectionLocal
            hotProduct={pageState.drivingData.hot_product}
            deviceType={deviceType}
            hotProducts={pageState.hotProducts}
            isFromCache={isFromCache}
            trackKnowledgeTopicPageProductExp={trackKnowledgeTopicPageProductExp}
            trackKnowledgeTopicPageProductClick={trackKnowledgeTopicPageProductClick}
          />

          {/* 眼镜优势介绍模块 */}
          <DrivingBenefit
            drivingData={pageState.drivingData}
            isMobile={isMobile}
            isFromCache={isFromCache}
            onExposure={(section) => {
              trackKnowledgeTopicPageMktExp({
                mkt_name: `${section}`,
              });
            }}
          />

          {/* Step Box - PC端显示 */}
          {!isMobile && pageState.drivingData.customize_glasses && (
            <StepBox
              stepData={pageState.drivingData.customize_glasses}
              isMobile={isMobile}
              onExposure={(mkName) => {
                trackKnowledgeTopicPageMktExp({
                  mkt_name: mkName,
                });
              }}
            />
          )}
        </div>

        {/* FAQ 部分 - 移动端 */}
        {isMobile && pageState.drivingData.faq_part && (
          <FaqAccordion
            faqData={{
              title: pageState.drivingData.faq_part.title || 'FAQ',
              data_array: pageState.drivingData.faq_part.faq_list || [],
            }}
            className="!bg-white"
            onExposure={() => {
              trackKnowledgeTopicPageMktExp({
                mkt_name: 'FAQ',
              });
            }}
          />
        )}

        {/* Step Box - 移动端显示 */}
        {isMobile && pageState.drivingData.customize_glasses && (
          <StepBox
            stepData={pageState.drivingData.customize_glasses}
            isMobile={isMobile}
            onExposure={(mkName) => {
              trackKnowledgeTopicPageMktExp({
                mkt_name: mkName,
              });
            }}
          />
        )}

        {/* 商品列表部分 - 集成ActivityProductGrid */}
        <ProductListSection
          listPart={pageState.drivingData.list_part}
          deviceType={deviceType}
          trackKnowledgeTopicPageProductExp={trackKnowledgeTopicPageProductExp}
          trackKnowledgeTopicPageProductClick={trackKnowledgeTopicPageProductClick}
        />

        {/* FAQ 部分 - PC端 */}
        {!isMobile && pageState.drivingData.faq_part && (
          <FaqAccordion
            faqData={{
              title: pageState.drivingData.faq_part.title || 'FAQ',
              data_array: pageState.drivingData.faq_part.faq_list || [],
            }}
            className="!bg-white"
            onExposure={() => {
              trackKnowledgeTopicPageMktExp({
                mkt_name: 'FAQ',
              });
            }}
          />
        )}
        <StaticFaqForSeo
          faqData={{
            title: pageState.drivingData.faq_part.title || 'FAQ',
            data_array: pageState.drivingData.faq_part.faq_list || [],
          }}
        />
      </div>
    </>
  );
}

// 热卖产品模块 - 使用通用组件
function HotProductSectionLocal({
  hotProduct,
  deviceType,
  hotProducts,
  isFromCache,
  trackKnowledgeTopicPageProductExp,
  trackKnowledgeTopicPageProductClick,
}: {
  hotProduct: any;
  deviceType: EPlatform;
  hotProducts: any[];
  isFromCache: boolean;
  trackKnowledgeTopicPageProductExp: (params: any) => void;
  trackKnowledgeTopicPageProductClick: (params: any) => void;
}) {
  if (!hotProduct?.group) {
    return null;
  }

  const handleProductClick = (product: any, index: number) => {
    trackKnowledgeTopicPageProductClick({
      item: product,
      index,
      mkt_name: '热卖商品位',
    });
  };

  const handleProductExp = (product: any, index: number) => {
    trackKnowledgeTopicPageProductExp({
      item: product,
      index,
      mkt_name: '热卖商品位',
    });
  };

  return (
    <HotProductSection
      hotProduct={hotProduct}
      hotProducts={hotProducts}
      deviceType={deviceType}
      isFromCache={isFromCache}
      theme="driving"
      onProductClick={handleProductClick}
      onProductExp={handleProductExp}
    />
  );
}

// 商品列表模块
function ProductListSection({
  listPart,
  deviceType,
  trackKnowledgeTopicPageProductExp,
  trackKnowledgeTopicPageProductClick,
}: {
  listPart: any;
  deviceType: EPlatform;
  trackKnowledgeTopicPageProductExp: (params: any) => void;
  trackKnowledgeTopicPageProductClick: (params: any) => void;
}) {
  if (!listPart?.group) {
    return null;
  }

  const isMobile = deviceType !== EPlatform.WEB;

  return (
    <div className="bg-[#ffffff]">
      <ActivityProductGrid
        id="more-driving-glasses"
        title={listPart.title || 'More Driving Glasses'}
        apiConfig={{
          group_id: listPart.group,
          limit: 12, // 更多商品数量
        }}
        showSort
        // 移动端使用无限滚动，PC端使用分页器
        showInfiniteScroll={isMobile}
        showPagination={!isMobile}
        deviceType={deviceType}
        wapColumns={2}
        pcColumns={{ lg: 3, xl: 4, '2xl': 4 }}
        padding={{
          wap: 'px-[14px]',
          pc: 'px-[48px] lg:px-[100px]',
        }}
        productCardProps={{
          showTryOn: false,
          showCorrelation: true,
          showReviewStar: true,
          className:
            deviceType !== EPlatform.WEB
              ? 'bg-none'
              : 'bg-white rounded-lg hover:shadow-lg transition-shadow',
          imageClassName: deviceType !== EPlatform.WEB ? 'h-[176px] rounded-[12px]' : undefined,
        }}
        onProductClick={(product, index) => {
          trackKnowledgeTopicPageProductClick({
            item: product,
            index,
            mkt_name: '商品位',
          });
        }}
        onProductExp={(product, index) => {
          trackKnowledgeTopicPageProductExp({
            item: product,
            index,
            mkt_name: '商品位',
          });
        }}
      />
    </div>
  );
}
